/**
 * 시도 패널 (DESIGN §8.6.9).
 *
 * **경계 폴리곤 없이 시도를 구분하는 수단**이다. `admin_2026.geojson` 이 없어
 * 게이트 D7 이 강등돼 있지만, 시도 구분에 경계선이 반드시 필요한 것은 아니다 —
 * `dim_charger.zcode` 가 524,643행 전부 채워져 있고 `/metrics/ranking?entity=zcode`
 * 가 이미 동작한다. 백엔드 변경 0.
 *
 * 행을 누르면 그 시도로 지도를 좁힌다. 16개 시도 중 14개가 POINT_CAP(5만)
 * 아래라 **그 시도 전체가 개별 충전기로 보인다** (경기·서울만 강등 유지).
 *
 * 커버리지 미달로 순위에서 분리된 시도도 **지우지 않는다.** 회색 + 사유로
 * 남긴다 — "판정 보류"와 "데이터 없음"은 다르다 (§3.7.1 · CLAUDE.md 절대원칙 2).
 *
 * 행 막대는 `CoverageBar` 를 쓰지 않는다. 그것은 `meta` 를 받아 `role="meter"`
 * 를 그리는 페이지·위젯 헤더용이라, 16행에 반복하면 미터가 16개가 된다.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchEnvelope, fetchPlain } from '@/lib/api'
import { int, pct } from '@/lib/fmt'
import type { Envelope } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './SidoPanel.css'

interface RankRow {
  entity_id: string
  n_chargers: number
  availability: number | null
  coverage: number | null
}

interface RankData {
  entity: string
  ranking: RankRow[]
  excluded: RankRow[]
}

interface FilterOptions {
  options: Record<string, { code: string; label: string; n: number | null }[]>
}

export function SidoPanel() {
  const filters = useFilters()
  const picked = filters.zcode

  // FilterBar 와 같은 키·같은 queryFn — 캐시를 공유해 요청이 두 번 나가지 않는다
  const { data: names } = useQuery({
    queryKey: ['meta', 'filters'],
    queryFn: ({ signal }) => fetchPlain<FilterOptions>('/meta/filters', signal),
    staleTime: 5 * 60 * 1000,
  })

  const label = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of names?.options?.zcode ?? []) m.set(o.code, o.label)
    return m
  }, [names])

  /**
   * **자기 축은 자기 목록에서 빼고 묻는다.**
   * zcode 를 그대로 넣으면 제주를 고른 순간 목록이 제주 한 줄로 줄어 다른
   * 시도로 옮겨 갈 방법이 사라진다. 다른 축(사업자·제조사…)은 그대로 반영해
   * "이 조건에서 시도별로 어떤가"를 답한다.
   */
  const listFilters = useMemo(() => ({ ...filters, zcode: [] }), [filters])

  const q = useQuery({
    queryKey: ['metrics/ranking', 'zcode', filterKey(listFilters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankData>(
        '/metrics/ranking',
        toQueryFilters(listFilters),
        { entity: 'zcode' },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<RankData> | undefined

  /** 순위와 분리된 것을 **한 표에** 둔다. 분리는 숨김이 아니다 */
  const rows = useMemo(() => {
    const rank = (env?.data.ranking ?? []).map((r) => ({ ...r, held: false }))
    const held = (env?.data.excluded ?? []).map((r) => ({ ...r, held: true }))
    return [...rank, ...held].sort((a, b) => b.n_chargers - a.n_chargers)
  }, [env])

  return (
    <section className="sidop" aria-labelledby="sidop-h">
      <h2 className="sidop__h" id="sidop-h">
        시도
      </h2>
      <p className="sidop__lead">
        행을 누르면 그 시도를 더하거나 뺍니다 — 여러 시도를 함께 볼 수 있습니다. 대부분은
        충전기 하나하나로 그려지고, 고른 것을 합쳐 5만기를 넘으면 육각형으로 강등됩니다.
      </p>

      {q.isLoading && <p className="sidop__msg">불러오는 중…</p>}
      {q.error != null && <p className="sidop__msg">시도 집계를 불러오지 못했습니다.</p>}

      <ul className="sidop__list">
        {rows.map((r) => {
          const on = picked.includes(r.entity_id)
          const cov = r.coverage ?? 0
          return (
            <li key={r.entity_id}>
              <button
                type="button"
                className={`sidop__row${on ? ' is-on' : ''}${r.held ? ' is-held' : ''}`}
                aria-pressed={on}
                onClick={() => filters.toggleList('zcode', r.entity_id)}
              >
                <span className="sidop__name">{label.get(r.entity_id) ?? r.entity_id}</span>
                <span className="sidop__n num">{int(r.n_chargers)}</span>
                <span
                  className="sidop__bar hatch-missing"
                  aria-hidden="true"
                  title={`관측 커버리지 ${pct(cov, 1)}`}
                >
                  <span className="sidop__fill" style={{ width: `${cov * 100}%` }} />
                </span>
                <span className="sidop__cov num">{pct(cov, 0)}</span>
                {r.held && <span className="sidop__held">판정 보류</span>}
              </button>
            </li>
          )
        })}
      </ul>

      {rows.some((r) => r.held) && (
        <p className="sidop__note">
          <strong>판정 보류</strong>는 나쁘다는 뜻이 아닙니다 — 관측 커버리지가 기준(
          {pct(filters.minCoverage, 0)})에 못 미쳐 순위에서 분리했을 뿐이고, 값은 지우지
          않았습니다.
        </p>
      )}

      <p className="sidop__note">
        시도 <strong>경계선</strong>은 그리지 않습니다 — 경계 파일(admin_2026.geojson)이
        없어서입니다. 그래서 충전기가 0기인 지역은 지도에 아예 나타나지 않습니다.
      </p>
    </section>
  )
}
